import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useAppSelector } from "../../../../store/types";
import type { Panel, PanelItem } from "../../../../components/SbuxDrawer";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SbuxTranslate from "../../../../components/SbuxTranslate";
import { IconButton } from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import { Box, Link } from "@mui/material";
import {
  STOPCONTACTCALLBACK_RECORDING_FLAG,
  RESOURCE_MANAGER_FLAG,
  TEST_SMS_FLAG,
  LEX_BOTS_FLAG,
} from "../../../../constants/index";
import { selectFeatureFlags } from "../../../../selectors/featureFlagSelector";

interface NavItem {
  name: string;
  url?: string;
  panels?: NavItem[];
  [key: string]: unknown;
}

interface ExternalItem {
  name: string;
  isUrlClicked: boolean;
}

/* eslint-disable react-hooks/rules-of-hooks */
export const panelData = (
  connectedInstance: Record<string, unknown> | null,
  managerNavData: Record<string, unknown>,
  showOtherIndicator: boolean,
  handleOtherClick: () => void,
  handleItemClick: (name: string) => void,
  selectedIndicator: Record<string, boolean>,
) => {
  if (!connectedInstance) return [];

  const {
    appConfig: { managerNav: instanceManagerNav },
  } = connectedInstance as { appConfig: { managerNav: NavItem[] } };

  const featureFlags = useAppSelector(selectFeatureFlags);

  const [selectedExternalList, setSelectedExternalList] = useState<
    ExternalItem[]
  >([]);
  const [selectedPanel, setSelectedPanel] = useState<ExternalItem | null>(null);
  const [stopCallbackEnabled, setStopCallbackEnabled] = useState(false);
  const [resourceManagerEnabled, setResourceManagerEnabled] = useState(false);
  const [testSmsEnabled, setTestSmsEnabled] = useState(false);
  const [lexBotsEnabled, setLexBotsEnabled] = useState(false);

  useEffect(() => {
    if (!featureFlags || !featureFlags.length) return;

    const flagSetters: Record<string, (val: boolean) => void> = {
      [STOPCONTACTCALLBACK_RECORDING_FLAG]: setStopCallbackEnabled,
      [RESOURCE_MANAGER_FLAG]: setResourceManagerEnabled,
      [TEST_SMS_FLAG]: setTestSmsEnabled,
      [LEX_BOTS_FLAG]: setLexBotsEnabled,
    };

    Object.entries(flagSetters).forEach(([flag, setter]) => {
      const featureFlag = featureFlags.find(
        (ff) => (ff as Record<string, unknown>).label === flag,
      );
      if (featureFlag) {
        setter(featureFlag.enabled);
      }
    });
  }, [featureFlags]);

  useEffect(() => {
    const result: ExternalItem[] = [];
    Object.keys(managerNavData).forEach((m) => {
      result.push({
        name: m,
        isUrlClicked: false,
      });
    });
    setSelectedExternalList(result);
  }, []);

  const linkRenderer = (panelUrl: string, panelName: string) => {
    const updateSelectedExternalList = [...selectedExternalList];
    updateSelectedExternalList.forEach((item, index) => {
      if (item.name === panelName) {
        item.isUrlClicked = true;
        selectedExternalList[index] = item;
      } else {
        item.isUrlClicked = false;
        selectedExternalList[index] = item;
      }
    });
    const selectedPanelObject = updateSelectedExternalList.find(
      (f) => f.name === panelName,
    );
    setSelectedExternalList(updateSelectedExternalList);
    selectedPanelObject && setSelectedPanel(selectedPanelObject);
    handleOtherClick();
    const newTab = window.open(panelUrl, "_blank", "noopener,noreferrer");
    newTab && newTab.focus();
  };

  const setNavigationUrl = (name: string) => {
    const isSelected = selectedIndicator && name && selectedIndicator[name];
    return (
      <Box
        sx={{
          display: "flex",
          width: 180,
          alignItems: "center",
        }}
      >
        <Link
          component="button"
          onClick={() => handleItemClick(name)}
          sx={{
            outline: "none !important",
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            component="span"
            sx={{
              display: "block",
              width: "100%",
              paddingTop: "1px",
              color: isSelected ? "#000000" : "#808080",
              textAlign: "left",
              overflowWrap: "break-word",
              wordWrap: "break-word",
            }}
          >
            <SbuxTranslate>{name}</SbuxTranslate>
          </Box>
        </Link>
        <ChevronRightIcon
          sx={{
            fontSize: 21,
            paddingTop: 0.2,
            flexShrink: 0,
            visibility: isSelected ? "visible" : "hidden",
          }}
        />
      </Box>
    );
  };

  const getNavObject = (panel: NavItem) => {
    const { name, url } = panel;

    if (name === "Callback Management" && !stopCallbackEnabled) {
      return null;
    }

    if (name === "Resource Manager" && !resourceManagerEnabled) {
      return null;
    }

    if (name === "Test SMS" && !testSmsEnabled) {
      return null;
    }

    if (name === "Lex Bots" && !lexBotsEnabled) {
      return null;
    }

    switch (name) {
      case "Feature Flags":
      case "Main Hours":
      case "Resource Manager":
      case "Closure Messages":
      case "Test SMS":
      case "Data Management":
      case "Lex Bots":
      case "Callback Management":
      case "Media Management":
      case "Temporary Messages":
      case "Routing Profiles Delete": {
        return setNavigationUrl(name);
      }

      default: {
        const isExternalSelected =
          showOtherIndicator && selectedPanel && selectedPanel.name === name;
        return (
          <Box
            sx={{
              display: "flex",
              width: 180,
              alignItems: "flex-start",
            }}
          >
            <Link
              component="button"
              sx={{
                outline: "none !important",
                textDecoration: "none",
                display: "flex",
                alignItems: "flex-start",
                flex: 1,
                minWidth: 0,
              }}
              onClick={() => linkRenderer(url ?? "", name)}
            >
              <Box
                component="span"
                sx={{
                  display: "block",
                  width: "100%",
                  paddingTop: 1,
                  overflowWrap: "break-word",
                  wordWrap: "break-word",
                  color: isExternalSelected ? "#000000" : "#808080",
                  textAlign: "left",
                }}
              >
                <SbuxTranslate>{name}</SbuxTranslate>
              </Box>
            </Link>
            {url !== undefined && url !== "" && (
              <IconButton
                aria-label="external-link-icon"
                sx={{
                  padding: 0,
                  flexShrink: 0,
                  alignSelf: "flex-start",
                  marginTop: "6px",
                }}
              >
                <LaunchIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
        );
      }
    }
  };

  const getPanelsData = (panels: NavItem[]): PanelItem[] => {
    const result = panels.reduce<PanelItem[]>((panelAcc, panelCur) => {
      const panelObj = Object.keys(managerNavData)?.find(
        (f) => f === panelCur.name,
      );
      panelObj &&
        managerNavData[panelObj] &&
        panelAcc.push({
          ...panelCur,
          url: getNavObject(panelCur),
        });
      return panelAcc;
    }, []);
    return result;
  };

  const panelsInfo = instanceManagerNav.reduce<Panel[]>((acc, cur) => {
    acc.push({
      ...cur,
      id: (cur.id ?? cur.name) as string | number,
      name: <SbuxTranslate>{cur.name}</SbuxTranslate>,
      panels: getPanelsData(cur.panels ?? []),
    });
    return acc;
  }, []);
  return panelsInfo;
};
/* eslint-enable react-hooks/rules-of-hooks */
